import { useEffect, useMemo } from 'react';
import { useFormRegistryAggregates } from '~/shared/forms/FormRegistryContext';

interface ValueToCopy {
	taskId: string;
	fieldName: string;
	value: unknown;
}

interface SelectedTasks {
	id: string;
	externalReferences: string[];
	documentationFormFields: { name: string }[];
}
export function ExternalReferenceValueCopier({ selectedTasks }: { selectedTasks: SelectedTasks[] }) {
	const formHelpers = useFormRegistryAggregates();
	const selectedTasksWithReferences = selectedTasks.filter(x => x.externalReferences && x.externalReferences.length > 0);

	const updatesToRun = useMemo(() => {
		const updates: ValueToCopy[] = [];
		for (const task of selectedTasksWithReferences) {
			for (const reference of task.externalReferences ?? []) {
				const taskWithReferenceValue = selectedTasks.find(x => x.documentationFormFields?.some(f => f.name === reference));
				if (!taskWithReferenceValue) {
					continue;
				}
				const referenceValues = formHelpers.get(taskWithReferenceValue.id)?.values[reference];
				const currentValues = formHelpers.get(task.id)?.values[reference];
				if (referenceValues && currentValues && referenceValues !== currentValues) {
					updates.push({ taskId: task.id, fieldName: reference, value: referenceValues });
				}
			}
		}
		return updates;
	}, [formHelpers, selectedTasks, selectedTasksWithReferences]);

	useEffect(() => {
		if (updatesToRun.length === 0) {
			return;
		}

		const groupedUpdates = updatesToRun.reduce((acc, update) => {
			if (!acc[update.taskId]) {
				acc[update.taskId] = [];
			}
			acc[update.taskId].push(update);
			return acc;
		}, {} as Record<string, ValueToCopy[]>);

		for (const taskId in groupedUpdates) {
			const form = formHelpers.get(taskId);
			if (!form) {
				continue;
			}
			const valuesToUpdate = groupedUpdates[taskId].reduce((acc, update) => {
				acc[update.fieldName] = update.value;
				return acc;
			}, {} as Record<string, unknown>);
			form.setValues(prevValues => ({ ...prevValues, ...valuesToUpdate }), true);
		}
	}, [updatesToRun, formHelpers]);

	return null;
}
